import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Gift, Bell, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const NewsletterSection = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle form submission (Zoho CRM integration placeholder)
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Simulate API call to Zoho CRM
    setTimeout(() => {
      setIsLoading(false);
      setIsSubscribed(true);
      setEmail('');
      
      // Reset success state after 3 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 3000);
    }, 1500);
  };

  // Floating newsletter icons
  const FloatingIcon = ({ Icon, delay = 0, x = "50%", size = 6 }) => (
    <motion.div
      animate={{ 
        y: [0, -15, 0],
        rotate: [0, 10, -10, 0]
      }}
      transition={{
        delay,
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className={`absolute w-${size} h-${size} text-mava-orange/20`}
      style={{ left: x, top: "20%" }}
    >
      <Icon className="w-full h-full" />
    </motion.div>
  );

  return (
    <motion.section
      ref={containerRef}
      className="relative py-20 lg:py-32 bg-gradient-to-br from-mava-off-white to-white overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        {/* Floating icons */}
        <FloatingIcon Icon={Mail} delay={0} x="15%" size={5} />
        <FloatingIcon Icon={Gift} delay={1} x="85%" size={6} />
        <FloatingIcon Icon={Bell} delay={2} x="25%" size={4} />
        <FloatingIcon Icon={CheckCircle} delay={1.5} x="75%" size={5} />

        {/* Animated background gradient */}
        <motion.div
          animate={{ 
            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 opacity-5"
          style={{
            background: "linear-gradient(45deg, #FF7A00, #00C6FF, #0072FF, #FF7A00)",
            backgroundSize: "400% 400%"
          }}
        />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-flex items-center justify-center w-16 h-16 bg-mava-gradient rounded-full mb-6 liquid-shadow"
            >
              <Mail className="w-8 h-8 text-white" />
            </motion.div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold text-mava-charcoal mb-6">
              Stay Refreshed
            </h2>
            <p className="text-xl font-inter text-gray-600 max-w-2xl mx-auto">
              Get the latest updates on new flavors, exclusive offers, and Mava moments
            </p>
            <div className="w-24 h-1 bg-mava-gradient mx-auto rounded-full mt-6" />
          </motion.div>

          {/* Newsletter Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid md:grid-cols-3 gap-8 mb-12"
          >
            {[
              {
                icon: Bell,
                title: "First to Know",
                description: "Be the first to hear about new product launches"
              },
              {
                icon: Gift,
                title: "Exclusive Offers",
                description: "Access special discounts and member-only deals"
              },
              {
                icon: CheckCircle,
                title: "Mava Moments",
                description: "Share your refreshing moments and win rewards"
              }
            ]?.map((benefit, index) => (
              <motion.div
                key={benefit?.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.2 }}
                className="text-center group"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full liquid-shadow mb-4 group-hover:bg-mava-orange brand-transition"
                >
                  <benefit.icon className="w-6 h-6 text-mava-orange group-hover:text-white brand-transition" />
                </motion.div>
                
                <h3 className="text-lg font-poppins font-bold text-mava-charcoal mb-2">
                  {benefit?.title}
                </h3>
                <p className="text-gray-600 font-inter">
                  {benefit?.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Newsletter Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-white rounded-3xl p-8 md:p-12 liquid-shadow"
          >
            {!isSubscribed ? (
              <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-poppins font-bold text-mava-charcoal mb-2">
                    Join the Mava Family
                  </h3>
                  <p className="text-gray-600 font-inter">
                    Subscribe to our newsletter and never miss a refreshing update
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      placeholder="Enter your email address"
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:border-mava-orange focus:ring-2 focus:ring-mava-orange/20 focus:outline-none brand-transition font-inter"
                      required
                    />
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute right-6 top-1/2 transform -translate-y-1/2"
                    >
                      <Mail className="w-5 h-5 text-gray-400" />
                    </motion.div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || !email}
                    className="w-full bg-mava-gradient hover:opacity-90 text-white py-4 text-lg font-poppins font-semibold rounded-2xl cursor-glow brand-transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="inline-block w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      <>
                        <span>Subscribe Now</span>
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="ml-2 inline-block"
                        >
                          →
                        </motion.div>
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-sm text-gray-500 text-center mt-4">
                  By subscribing, you agree to receive marketing emails from Mava. 
                  You can unsubscribe at any time.
                </p>
              </form>
            ) : (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md mx-auto"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6"
                >
                  <CheckCircle className="w-10 h-10 text-white" />
                </motion.div>

                <h3 className="text-2xl md:text-3xl font-poppins font-bold text-mava-charcoal mb-4">
                  Welcome to Mava! 🎉
                </h3>
                <p className="text-gray-600 font-inter mb-6">
                  Thank you for subscribing! You'll receive your first newsletter soon with 
                  exclusive updates and offers.
                </p>
                
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-6xl mb-4"
                >
                  💧
                </motion.div>
              </motion.div>
            )}
          </motion.div>

          {/* Social Proof */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-center mt-8"
          >
            <p className="text-gray-500 font-inter">
              Join <span className="font-poppins font-semibold text-mava-orange">10,000+</span> happy subscribers
            </p>
            
            {/* Subscriber avatars */}
            <div className="flex justify-center mt-4 space-x-2">
              {[...Array(5)]?.map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ delay: 1.4 + i * 0.1, type: "spring" }}
                  className="w-10 h-10 bg-mava-gradient rounded-full flex items-center justify-center text-white font-poppins font-semibold"
                >
                  {String.fromCharCode(65 + i)}
                </motion.div>
              ))}
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ delay: 1.9, type: "spring" }}
                className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-sm font-poppins"
              >
                +10K
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default NewsletterSection;